/**
 * Admin sidebar — portal-styled (logo + label + collapsible groups + user
 * profile bottom) but admin-specific.
 *
 * Modeled on apps/portal/src/components/layout/StandaloneSidebar.tsx but
 * stripped of dealer/tenant-aware concerns. The tenant-name slot is the
 * static label "Admin Console" since SUPER_ADMINs operate cross-tenant
 * and the JWT-derived tenant name would be misleading.
 *
 * Reuses portal's UI primitives (Avatar, Collapsible) directly so visual
 * styling matches the rest of the suite.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { tenantAPI } from '@vehix/portal/services/api/tenants';
import { zoneKeys } from '@vehix/shared/admin/hooks/useZones';
import {
  LayoutDashboard,
  Settings,
  Users,
  MapPin,
  List,
  Play,
  GitBranch,
  BarChart3,
  MessageCircle,
  ChevronDown,
  ChevronRight,
  Activity,
  Database,
  TrendingUp,
  SlidersHorizontal
} from 'lucide-react';
import { cn } from '@vehix/portal/lib/utils';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger
} from '@vehix/portal/components/ui/collapsible';
import { Avatar, AvatarFallback, AvatarImage } from '@vehix/portal/components/ui/avatar';
import { ProfileDialog } from '@vehix/portal/components/ProfileDialog';
import { useAuth } from '@vehix/shared/admin/portal-bridge';
import logo from '@vehix/portal/assets/logos/logo_transparent.png';

interface NavItem {
  label: string;
  to: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  icon: React.ComponentType<any>;
}

interface NavGroup {
  title: string;
  collapsible: boolean;
  items: NavItem[];
}

const NAV_GROUPS: NavGroup[] = [
  {
    title: 'Home',
    collapsible: false,
    items: [{ label: 'Dashboard', to: '/', icon: LayoutDashboard }]
  },
  {
    title: 'Setup',
    collapsible: true,
    items: [
      { label: 'Tenant Setup', to: '/setup/tenants', icon: Settings },
      { label: 'User Setup', to: '/setup/users', icon: Users },
      { label: 'Zone Map', to: '/setup/zones', icon: MapPin },
      { label: 'Zone List', to: '/setup/zone-list', icon: List },
      { label: 'Configurations', to: '/setup/configurations', icon: SlidersHorizontal }
    ]
  },
  {
    title: 'Sales',
    collapsible: true,
    items: [{ label: 'Sales Dashboard', to: '/sales/dashboard', icon: TrendingUp }]
  },
  {
    title: 'Workflow',
    collapsible: true,
    items: [
      { label: 'Workflow Management', to: '/workflow/management', icon: Play },
      { label: 'Pipelines', to: '/workflow/pipelines', icon: GitBranch }
    ]
  },
  {
    title: 'Monitoring',
    collapsible: true,
    items: [
      { label: 'Data Quality', to: '/monitoring/data-quality', icon: Database },
      { label: 'System Health', to: '/monitoring/system-health', icon: Activity }
    ]
  },
  {
    title: 'Analytics',
    collapsible: false,
    items: [{ label: 'User Analytics', to: '/analytics', icon: BarChart3 }]
  },
  {
    title: 'Feedback',
    collapsible: false,
    items: [{ label: 'Feedback', to: '/feedback', icon: MessageCircle }]
  }
];

function isActiveTo(currentPath: string, to: string): boolean {
  if (currentPath === to) return true;
  // Pipelines section matches /workflow/pipelines and any nested route
  // (/workflow/pipelines/schedules, /workflow/pipelines/:id).
  if (to === '/workflow/pipelines' && currentPath.startsWith('/workflow/pipelines')) return true;
  return false;
}

function groupContainsActive(group: NavGroup, currentPath: string): boolean {
  return group.items.some(item => isActiveTo(currentPath, item.to));
}

interface NavLinkItemProps {
  item: NavItem;
  isActive: boolean;
}

const NavLinkItem: React.FC<NavLinkItemProps> = ({ item, isActive }) => {
  const queryClient = useQueryClient();

  // Prefetch the all-zones query when the user hovers the Zone List link.
  // The natural pause between hover and click (~200-400ms) is enough time to
  // start (and often complete) the fetch, so by the time the user clicks,
  // the data is already in React Query's cache and the page renders
  // instantly. Industry-standard pattern (Linear, Notion, Vercel) for the
  // perception of "instant navigation."
  //
  // Only enabled for /setup/zone-list today; if other routes benefit from
  // the same trick later, we can generalize NavItem with a `prefetch`
  // callback. YAGNI for now.
  const handlePointerEnter = useCallback(() => {
    if (item.to !== '/setup/zone-list') return;
    void queryClient.prefetchQuery({
      queryKey: zoneKeys.lists(),
      queryFn: () => tenantAPI.getAllZonesWithZipCounts(),
      staleTime: 10 * 60 * 1000
    });
  }, [item.to, queryClient]);

  return (
    <NavLink
      to={item.to}
      onPointerEnter={handlePointerEnter}
      className={cn(
        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm transition-all duration-200 relative group',
        'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
        isActive
          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-semibold shadow-sm border-l-4 border-l-primary pl-2'
          : 'hover:pl-3 border-l-4 border-l-transparent'
      )}
    >
      <item.icon
        className={cn(
          'h-4 w-4 flex-shrink-0 transition-colors',
          isActive ? 'text-primary' : 'text-sidebar-foreground/70 group-hover:text-primary'
        )}
      />
      <span className='truncate'>{item.label}</span>
      {isActive && <div className='absolute inset-0 bg-primary/5 rounded-lg -z-10' />}
    </NavLink>
  );
};

interface UserProfileFooterProps {
  email: string;
  displayName: string;
  initials: string;
  avatarUrl?: string;
  onClick: () => void;
}

const UserProfileFooter: React.FC<UserProfileFooterProps> = ({
  email,
  displayName,
  initials,
  avatarUrl,
  onClick
}) => (
  <div className='border-t border-sidebar-border p-3 bg-sidebar-accent/30'>
    <button
      onClick={onClick}
      className='flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-sidebar-accent w-full text-left transition-all duration-200 group cursor-pointer'
    >
      <Avatar className='h-8 w-8 flex-shrink-0 ring-2 ring-primary/30 group-hover:ring-primary/60 transition-all'>
        {avatarUrl && <AvatarImage src={avatarUrl} alt={displayName} />}
        <AvatarFallback className='text-xs bg-primary text-white font-semibold'>
          {initials}
        </AvatarFallback>
      </Avatar>
      <div className='flex-1 min-w-0'>
        <div className='text-sm font-semibold text-sidebar-foreground truncate'>{displayName}</div>
        <div className='text-xs text-sidebar-foreground/50 truncate'>{email}</div>
      </div>
    </button>
  </div>
);

// eslint-disable-next-line max-lines-per-function
export const AdminSidebar: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);

  // Collapsible group open-state: auto-expand if any child route is active.
  // Indices match NAV_GROUPS order: [0]=Home, [1]=Setup, [2]=Workflow.
  const [setupOpen, setSetupOpen] = useState(() =>
    groupContainsActive(NAV_GROUPS[1]!, location.pathname)
  );
  const [workflowOpen, setWorkflowOpen] = useState(() =>
    groupContainsActive(NAV_GROUPS[2]!, location.pathname)
  );

  useEffect(() => {
    if (groupContainsActive(NAV_GROUPS[1]!, location.pathname)) setSetupOpen(true);
    if (groupContainsActive(NAV_GROUPS[2]!, location.pathname)) setWorkflowOpen(true);
  }, [location.pathname]);

  const email = user?.email ?? '';
  const firstName = userProfile?.firstName ?? '';
  const lastName = userProfile?.lastName ?? '';
  const displayName = [firstName, lastName].filter(Boolean).join(' ') || email.split('@')[0] || 'Admin';
  const initials =
    (firstName[0] ?? '').toUpperCase() + (lastName[0] ?? '').toUpperCase() ||
    (email[0] ?? 'A').toUpperCase();
  const avatarUrl =
    (userProfile as unknown as { profile_photo_url?: string })?.profile_photo_url ?? undefined;

  const renderGroup = (group: NavGroup) => {
    const isOpen =
      group.title === 'Setup' ? setupOpen : group.title === 'Workflow' ? workflowOpen : true;
    const setOpen =
      group.title === 'Setup'
        ? setSetupOpen
        : group.title === 'Workflow'
          ? setWorkflowOpen
          : undefined;

    if (group.collapsible && setOpen) {
      return (
        <Collapsible key={group.title} open={isOpen} onOpenChange={setOpen} className='mb-4'>
          <CollapsibleTrigger asChild>
            <button className='w-full flex items-center justify-between px-3 py-2 mb-2 rounded-lg text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider hover:bg-sidebar-accent/50 transition-colors'>
              <span>{group.title}</span>
              {isOpen ? (
                <ChevronDown className='h-3 w-3' />
              ) : (
                <ChevronRight className='h-3 w-3' />
              )}
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <div className='space-y-0.5'>
              {group.items.map(item => (
                <NavLinkItem
                  key={item.to}
                  item={item}
                  isActive={isActiveTo(location.pathname, item.to)}
                />
              ))}
            </div>
          </CollapsibleContent>
        </Collapsible>
      );
    }

    return (
      <div key={group.title} className='mb-4'>
        <div className='text-xs font-semibold text-sidebar-foreground/50 uppercase tracking-wider mb-2 px-3'>
          {group.title}
        </div>
        <div className='space-y-0.5'>
          {group.items.map(item => (
            <NavLinkItem
              key={item.to}
              item={item}
              isActive={isActiveTo(location.pathname, item.to)}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <aside className='hidden md:flex w-64 flex-col border-r bg-sidebar text-sidebar-foreground overflow-hidden'>
      {/* Header — circular logo + brand label + admin slot. Logo navigates to dashboard. */}
      <div className='flex flex-col items-center gap-3 px-4 py-5 border-b border-sidebar-border'>
        <button
          onClick={() => navigate('/')}
          className='flex flex-col items-center gap-3 hover:opacity-90 transition-all duration-200 hover:scale-105'
          aria-label='Go to dashboard'
        >
          <div className='rounded-full bg-primary-foreground shadow-lg flex items-center justify-center overflow-hidden ring-2 ring-primary/20 w-16 h-16'>
            <img src={logo} alt='Vehix.ai' className='object-cover h-12 w-12' />
          </div>
          <div className='text-center'>
            <div className='font-bold text-lg text-sidebar-foreground'>Vehix.ai</div>
            <div className='text-xs text-sidebar-foreground/60'>Admin Console</div>
          </div>
        </button>
      </div>

      {/* Navigation groups */}
      <nav className='flex-1 overflow-y-auto p-2'>{NAV_GROUPS.map(renderGroup)}</nav>

      {/* User profile footer — clicking opens the Profile + Appearance dialog */}
      <UserProfileFooter
        email={email}
        displayName={displayName}
        initials={initials || 'A'}
        onClick={() => setProfileDialogOpen(true)}
        {...(avatarUrl && { avatarUrl })}
      />

      <ProfileDialog open={profileDialogOpen} onOpenChange={setProfileDialogOpen} />
    </aside>
  );
};
